import React, { useEffect } from 'react';
import '../styles/TermsPage.css';

/**
 * Privacy Policy — content is reproduced verbatim from the text supplied by
 * VIN WHOLESALE. Do not edit the wording. (Reuses the shared legal-page
 * styles from TermsPage.css.)
 */
const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <h1 className="terms-page__title">VIN WHOLESALE PRIVACY POLICY</h1>
      <p className="terms-page__subtitle">Effective Date: June 2026</p>

      <p>
        VIN WHOLESALE (“VIN”, “VIN WHOLESALE”, “we”, “our”, or “us”) respects your privacy and is
        committed to protecting the information you provide when using our website, services,
        applications, and ordering platforms.
      </p>
      <p>This Privacy Policy explains how we collect, use, store, and protect your information.</p>
      <p>
        By using our website, creating an account, submitting information, or purchasing products
        from VIN WHOLESALE, you agree to the practices described in this Privacy Policy.
      </p>

      <hr />

      <section className="terms-page__section">
        <h2>Information We Collect</h2>
        <p>
          As a business-to-business wholesale distributor, we collect information necessary to verify
          businesses, process orders, provide customer support, and improve our services.
        </p>
        <p>Information we may collect includes:</p>

        <h3>Business Information</h3>
        <ul>
          <li>Business name</li>
          <li>DBA name</li>
          <li>Business license</li>
          <li>Tobacco permits and licenses</li>
          <li>Resale certificates</li>
          <li>Tax documentation</li>
          <li>Employer Identification Number (EIN)</li>
          <li>Business verification documents</li>
        </ul>

        <h3>Contact Information</h3>
        <ul>
          <li>Owner name</li>
          <li>Buyer name</li>
          <li>Authorized purchaser information</li>
          <li>Billing address</li>
          <li>Shipping address</li>
          <li>Phone number</li>
          <li>Mobile phone number</li>
          <li>Email address</li>
        </ul>

        <h3>Payment Information</h3>
        <ul>
          <li>Credit card information</li>
          <li>Debit card information</li>
          <li>ACH information</li>
          <li>Payment history</li>
          <li>Transaction records</li>
        </ul>
        <p>
          Payment information is processed through secure third-party payment providers and is not
          stored in full on VIN WHOLESALE systems unless required for authorized business purposes.
        </p>

        <h3>Website and Technical Information</h3>
        <ul>
          <li>IP address</li>
          <li>Device information</li>
          <li>Browser information</li>
          <li>Website activity</li>
          <li>Login history</li>
          <li>Order history</li>
          <li>Referral sources</li>
          <li>Geographic region</li>
        </ul>

        <h3>Marketing Information</h3>
        <ul>
          <li>Email preferences</li>
          <li>SMS preferences</li>
          <li>Survey responses</li>
          <li>Promotional campaign participation</li>
        </ul>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Why We Collect Information</h2>
        <p>We use information collected to:</p>
        <ul>
          <li>Verify business accounts</li>
          <li>Process and fulfill orders</li>
          <li>Deliver products</li>
          <li>Provide customer support</li>
          <li>Communicate regarding orders</li>
          <li>Prevent fraud</li>
          <li>Improve website performance</li>
          <li>Improve customer experience</li>
          <li>Comply with legal requirements</li>
          <li>Conduct internal reporting and analytics</li>
          <li>Manage promotions and marketing programs</li>
        </ul>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Business Account Verification</h2>
        <p>VIN WHOLESALE is a wholesale distributor that sells only to qualified businesses.</p>
        <p>
          To comply with applicable laws and industry requirements, VIN WHOLESALE may collect and
          retain:
        </p>
        <ul>
          <li>Business licenses</li>
          <li>Tobacco licenses</li>
          <li>Resale certificates</li>
          <li>Government-issued identification</li>
          <li>Additional verification documents</li>
        </ul>
        <p>
          These records may be maintained for compliance, auditing, tax, legal, operational, and
          account verification purposes.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Email Communications</h2>
        <p>
          By creating an account, requesting information, placing an order, or providing your email
          address, you consent to receive communications from VIN WHOLESALE.
        </p>
        <p>These communications may include:</p>
        <ul>
          <li>Order confirmations</li>
          <li>Shipping notifications</li>
          <li>Invoices</li>
          <li>Account notices</li>
          <li>Product announcements</li>
          <li>Promotional offers</li>
          <li>Industry updates</li>
        </ul>
        <p>
          You may unsubscribe from promotional emails at any time using the unsubscribe link included
          in our emails.
        </p>
        <p>
          Operational and transactional communications may continue as necessary to service your
          account.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>SMS and Mobile Communications</h2>
        <p>If you provide a mobile phone number, you consent to receive:</p>
        <ul>
          <li>Order updates</li>
          <li>Delivery notifications</li>
          <li>Account notices</li>
          <li>Promotional messages</li>
          <li>Marketing communications</li>
        </ul>
        <p>Message and data rates may apply.</p>
        <p>You may opt out of marketing text messages at any time by replying STOP.</p>
        <p>
          Transactional communications related to active orders may still be sent when necessary.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Information Sharing</h2>
        <p>VIN WHOLESALE does not sell customer information to third parties.</p>
        <p>We may share information with:</p>

        <h3>Service Providers</h3>
        <ul>
          <li>Payment processors</li>
          <li>Shipping carriers</li>
          <li>Technology providers</li>
          <li>Website hosting providers</li>
          <li>Email service providers</li>
          <li>SMS communication providers</li>
        </ul>

        <h3>Legal Requirements</h3>
        <p>We may disclose information when required to:</p>
        <ul>
          <li>Comply with applicable laws</li>
          <li>Respond to legal requests</li>
          <li>Protect our rights</li>
          <li>Prevent fraud</li>
          <li>Enforce our policies</li>
        </ul>

        <h3>Business Transactions</h3>
        <p>
          In the event of a merger, acquisition, sale, restructuring, or transfer of assets, customer
          information may be transferred as part of the transaction.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Data Security</h2>
        <p>
          VIN WHOLESALE maintains reasonable administrative, technical, and physical safeguards
          designed to protect customer information.
        </p>
        <p>These safeguards include:</p>
        <ul>
          <li>Secure servers</li>
          <li>Password protection</li>
          <li>Restricted employee access</li>
          <li>Encryption technologies where appropriate</li>
          <li>Fraud monitoring systems</li>
        </ul>
        <p>
          No method of transmission or storage is completely secure. While we strive to protect your
          information, we cannot guarantee absolute security.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Cookies and Website Technologies</h2>
        <p>VIN WHOLESALE uses cookies and similar technologies to:</p>
        <ul>
          <li>Improve website performance</li>
          <li>Remember user preferences</li>
          <li>Analyze website traffic</li>
          <li>Improve user experience</li>
          <li>Support security measures</li>
          <li>Track website effectiveness</li>
        </ul>
        <p>
          Users may disable cookies through browser settings; however, certain website functions may
          not operate properly.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Third-Party Links</h2>
        <p>Our website may contain links to third-party websites.</p>
        <p>
          VIN WHOLESALE is not responsible for the privacy practices, content, or security of
          third-party websites.
        </p>
        <p>Users should review the privacy policies of those websites before providing information.</p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Data Retention</h2>
        <p>VIN WHOLESALE retains information as long as reasonably necessary to:</p>
        <ul>
          <li>Maintain business records</li>
          <li>Fulfill legal obligations</li>
          <li>Resolve disputes</li>
          <li>Enforce agreements</li>
          <li>Support ongoing business operations</li>
        </ul>
        <p>
          Verification documents, tax records, and transaction histories may be retained for extended
          periods as required by law or business necessity.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Your Privacy Choices</h2>
        <p>You may request to:</p>
        <ul>
          <li>Update account information</li>
          <li>Correct inaccurate information</li>
          <li>Change communication preferences</li>
          <li>Opt out of marketing communications</li>
        </ul>
        <p>Requests may be submitted to VIN WHOLESALE customer service.</p>
        <p>
          Certain information may be retained as required by law, accounting requirements, fraud
          prevention efforts, or legitimate business purposes.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Children’s Privacy</h2>
        <p>VIN WHOLESALE is a business-to-business wholesale distributor.</p>
        <p>
          Our website and services are not intended for individuals under twenty-one (21) years of
          age.
        </p>
        <p>We do not knowingly collect information from minors.</p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Changes to This Privacy Policy</h2>
        <p>VIN WHOLESALE reserves the right to modify this Privacy Policy at any time.</p>
        <p>Changes become effective immediately upon posting to the website.</p>
        <p>
          Continued use of our website, services, or account after updates constitutes acceptance of
          the revised Privacy Policy.
        </p>
      </section>

      <hr />

      <section className="terms-page__section">
        <h2>Contact Us</h2>
        <p>
          For questions regarding this Privacy Policy, account information, or communication
          preferences, please contact:
        </p>
        <p>
          VIN WHOLESALE
          <br />
          Email: <a href="mailto:info@vindistro.com">info@vindistro.com</a>
          <br />
          Phone: <a href="tel:5028074201">5028074201</a>
          <br />
          Website: <a href="https://www.vindistro.com" target="_blank" rel="noreferrer">www.vindistro.com</a>
        </p>
      </section>

      <p className="terms-page__updated">Last Updated: June 2026</p>
    </div>
  );
};

export default PrivacyPage;
